import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import Api from '@plone/volto/helpers/Api/Api';
import AppNav from '../AppNav/AppNav';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import {
  formatMatterTitleMessage,
  formatTokenMessage,
  sharedMessages,
} from '../../utils/uiMessages';

const messages = defineMessages({
  deleteMatterMessage: {
    id: 'Delete "{title}"? This cannot be undone.',
    defaultMessage: 'Delete "{title}"? This cannot be undone.',
  },
  deleteMatterTitle: {
    id: 'Delete matter?',
    defaultMessage: 'Delete matter?',
  },
  filterRegistryNote: {
    id: 'Filter the registry by reference, title, client, jurisdiction, status, or attorney.',
    defaultMessage:
      'Filter the registry by reference, title, client, jurisdiction, status, or attorney.',
  },
  loadingMatters: {
    id: 'Loading matters…',
    defaultMessage: 'Loading matters…',
  },
  matterLookupFailed: {
    id: 'Matter lookup failed with status {status}',
    defaultMessage: 'Matter lookup failed with status {status}',
  },
  matterRegistryNote: {
    id: 'Use matters as the main record for prosecution work, deadlines, and tool results.',
    defaultMessage:
      'Use matters as the main record for prosecution work, deadlines, and tool results.',
  },
  noMattersMatch: {
    id: 'No matters match the current search.',
    defaultMessage: 'No matters match the current search.',
  },
  noMattersYet: {
    id: 'No matters yet.',
    defaultMessage: 'No matters yet.',
  },
  searchMattersPlaceholder: {
    id: 'Search by matter reference, title, client, jurisdiction, status, or attorney',
    defaultMessage:
      'Search by matter reference, title, client, jurisdiction, status, or attorney',
  },
  shownCount: {
    id: '{count} shown',
    defaultMessage: '{count} shown',
  },
});

export default function MattersPage() {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('matter_reference_code');
  const [sortDirection, setSortDirection] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingPath, setDeletingPath] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  function authHeaders(extra = {}) {
    return token
      ? {
          ...extra,
          Authorization: `Bearer ${token}`,
        }
      : extra;
  }

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const response = await fetch(
          '/++api++/@search?portal_type=Matter&sort_on=sortable_title&sort_order=ascending&b_size=200&metadata_fields=matter_reference_code&metadata_fields=client_name&metadata_fields=jurisdiction&metadata_fields=matter_status&metadata_fields=responsible_attorney',
          { headers: authHeaders({ Accept: 'application/json' }) },
        );
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.matterLookupFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        if (active) {
          setItems(
            (payload.items || [])
              .filter((item) => item['@type'] === 'Matter')
              .map(normalizeMatterSearchItem),
          );
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [intl, token]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const searchableItems = query
      ? items.filter((item) =>
          [
            item.matter_reference_code,
            item.title,
            item.client_name,
            item.jurisdiction,
            formatTokenMessage(intl, item.matter_status),
            item.responsible_attorney,
          ]
            .join(' ')
            .toLowerCase()
            .includes(query),
        )
      : items;

    const sortedItems = [...searchableItems].sort((left, right) => {
      const leftValue = sortableMatterValue(left, sortKey);
      const rightValue = sortableMatterValue(right, sortKey);
      const comparison = leftValue.localeCompare(rightValue, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sortedItems;
  }, [intl, items, searchQuery, sortDirection, sortKey]);

  function onSort(nextKey) {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(nextKey);
    setSortDirection('asc');
  }

  async function onDelete(item) {
    const path = normalizeMatterPath(item['@id']);
    if (!path || path === '/') {
      setError(`Invalid matter path for delete: ${item['@id'] || '(missing)'}`);
      return;
    }

    setDeletingPath(path);
    setError('');

    try {
      const api = new Api();
      await api.del(path);
      setItems((current) =>
        current.filter(
          (currentItem) => normalizeMatterPath(currentItem['@id']) !== path,
        ),
      );
      setPendingDelete(null);
    } catch (deleteError) {
      const responseText =
        deleteError?.response?.text ||
        deleteError?.response?.body?.message ||
        deleteError?.response?.body?.error?.message ||
        deleteError?.message ||
        '';
      setError(
        `Delete failed with status ${
          deleteError.status || 'unknown'
        } for ${path}${responseText ? `: ${responseText}` : ''}`,
      );
    } finally {
      setDeletingPath('');
    }
  }

  return (
    <main className="patent-section">
      <section className="patent-section__shell">
        <AppNav currentPath="/matters" />
        <header className="patent-section__hero">
          <p className="patent-section__eyebrow">
            {intl.formatMessage(sharedMessages.matters)}
          </p>
          <h1>{intl.formatMessage(sharedMessages.titleMatterRegistry)}</h1>
          <p className="patent-section__note">
            {intl.formatMessage(messages.matterRegistryNote)}
          </p>
          <div className="patent-section__actions">
            <Link className="patent-section__action" to="/register-matter">
              {intl.formatMessage(sharedMessages.registerMatter)}
            </Link>
            <Link className="patent-section__ghost" to="/overview">
              {intl.formatMessage(sharedMessages.backToOverview)}
            </Link>
          </div>
        </header>

        <section className="patent-dashboard__filters">
          <div className="patent-dashboard__filters-head">
            <div>
              <h2>{intl.formatMessage(sharedMessages.titleMatterRegistry)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.filterRegistryNote)}
              </p>
            </div>
            {!loading ? (
              <span className="patent-section__note">
                {intl.formatMessage(messages.shownCount, {
                  count: filteredItems.length,
                })}
              </span>
            ) : null}
          </div>
          <div className="patent-dashboard__filter-grid">
            <label className="patent-dashboard__filter-field patent-entry__field--wide">
              <span className="patent-dashboard__filter-label">
                {intl.formatMessage(sharedMessages.searchMatters)}
              </span>
              <input
                className="patent-entry__input"
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={intl.formatMessage(messages.searchMattersPlaceholder)}
              />
            </label>
          </div>
        </section>

        {loading ? (
          <p className="patent-section__note">
            {intl.formatMessage(messages.loadingMatters)}
          </p>
        ) : null}
        {error ? <p className="patent-entry__error">{error}</p> : null}

        {!loading && !error ? (
          <div className="patent-matters-table">
            <div
              className="patent-matters-table__header patent-matters-table__row"
              role="row"
            >
              <SortHeader
                label={intl.formatMessage(sharedMessages.matter)}
                sortKey="matter_reference_code"
                activeKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortHeader
                label={intl.formatMessage(sharedMessages.client)}
                sortKey="client_name"
                activeKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortHeader
                label={intl.formatMessage(sharedMessages.jurisdiction)}
                sortKey="jurisdiction"
                activeKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortHeader
                label={intl.formatMessage(sharedMessages.status)}
                sortKey="matter_status"
                activeKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortHeader
                label={intl.formatMessage(sharedMessages.responsibleAttorney)}
                sortKey="responsible_attorney"
                activeKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <div className="patent-matters-table__header-cell patent-matters-table__header-cell--action">
                {intl.formatMessage(sharedMessages.actions)}
              </div>
            </div>
            {filteredItems.map((item) => (
              <article key={item['@id']} className="patent-matters-table__row">
                <div className="patent-matters-table__primary">
                  <Link
                    className="patent-matters-table__title"
                    to={matterEditorUrl(item['@id'])}
                  >
                    {item.matter_reference_code ||
                      intl.formatMessage(sharedMessages.notAssigned)}
                  </Link>
                  <p className="patent-matters-table__subtitle">
                    {formatMatterTitleMessage(intl, item.title)}
                  </p>
                </div>
                <div className="patent-matters-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.client)}
                  </span>
                  <strong>
                    {item.client_name || intl.formatMessage(sharedMessages.clientPending)}
                  </strong>
                </div>
                <div className="patent-matters-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.jurisdiction)}
                  </span>
                  <strong>{item.jurisdiction || '—'}</strong>
                </div>
                <div className="patent-matters-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.status)}
                  </span>
                  <strong>{formatTokenMessage(intl, item.matter_status)}</strong>
                </div>
                <div className="patent-matters-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.responsibleAttorney)}
                  </span>
                  <strong>{item.responsible_attorney || '—'}</strong>
                </div>
                <div className="patent-matters-table__cell patent-matters-table__cell--action">
                  <button
                    className="patent-entry__ghost"
                    type="button"
                    onClick={() => setPendingDelete(item)}
                    disabled={deletingPath === normalizeMatterPath(item['@id'])}
                  >
                    {deletingPath === normalizeMatterPath(item['@id'])
                      ? intl.formatMessage(sharedMessages.deleting)
                      : intl.formatMessage(sharedMessages.delete)}
                  </button>
                </div>
              </article>
            ))}
            {!filteredItems.length ? (
              <p className="patent-section__note">
                {items.length
                  ? intl.formatMessage(messages.noMattersMatch)
                  : intl.formatMessage(messages.noMattersYet)}
              </p>
            ) : null}
          </div>
        ) : null}
        <ConfirmDialog
          open={Boolean(pendingDelete)}
          title={intl.formatMessage(messages.deleteMatterTitle)}
          message={
            pendingDelete
              ? intl.formatMessage(messages.deleteMatterMessage, {
                  title:
                    pendingDelete.matter_reference_code ||
                    pendingDelete.title ||
                    intl.formatMessage(sharedMessages.matter),
                })
              : ''
          }
          confirming={
            pendingDelete
              ? deletingPath === normalizeMatterPath(pendingDelete['@id'])
              : false
          }
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => pendingDelete && onDelete(pendingDelete)}
        />
      </section>
    </main>
  );
}

function sortableMatterValue(item, key) {
  switch (key) {
    case 'title':
      return String(item.title || '').trim() || 'zzz';
    case 'client_name':
      return String(item.client_name || '').trim() || 'zzz';
    case 'jurisdiction':
      return String(item.jurisdiction || '').trim() || 'zzz';
    case 'matter_status':
      return String(item.matter_status || '').trim() || 'zzz';
    case 'responsible_attorney':
      return String(item.responsible_attorney || '').trim() || 'zzz';
    case 'matter_reference_code':
    default:
      return String(item.matter_reference_code || '').trim() || 'zzz';
  }
}

function normalizeMatterSearchItem(item) {
  return {
    ...item,
    matter_reference_code:
      item.matter_reference_code || item.matter_reference || '',
    client_name: item.client_name || '',
    jurisdiction: item.jurisdiction || '',
    matter_status: item.matter_status || '',
    responsible_attorney: item.responsible_attorney || '',
  };
}

function SortHeader({ label, sortKey, activeKey, direction, onSort }) {
  const active = activeKey === sortKey;
  return (
    <button
      className={`patent-matters-table__header-button${
        active ? ' patent-matters-table__header-button--active' : ''
      }`}
      type="button"
      onClick={() => onSort(sortKey)}
    >
      <span>{label}</span>
      <span className="patent-matters-table__sort-indicator">
        {active ? (direction === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function normalizeMatterPath(url) {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    const pathname = new URL(url || '', origin).pathname;
    if (!pathname || pathname === '/' || pathname === '/Plone') {
      return '';
    }
    return pathname.startsWith('/Plone/') ? pathname.replace(/^\/Plone/, '') : pathname;
  } catch {
    const raw = url || '';
    if (!raw || raw === '/' || raw === '/Plone') {
      return '';
    }
    return raw.startsWith('/Plone/') ? raw.replace(/^\/Plone/, '') : raw;
  }
}

function matterEditorUrl(url) {
  return `/matter-editor?path=${encodeURIComponent(normalizeMatterPath(url))}`;
}
